import { insertAtIndex } from '../utils/general';
import { PAGE_ABOUT, PAGE_BRAND_GIFT_CARDS, PAGE_EVENTS, PAGE_LOCATIONS, PAGE_PAGES, PAGE_PRESS } from './UriConstants';

// Pages whose Hide/Show is backed by a real backend visibility toggle (see src/api/pageVisibility.ts).
export type PageVisibilityKey = 'catering' | 'events' | 'careers';

// Stable identifier of a page, shared (by value) with the TapManager-Backend /pageOrder API and the
// TapTab website nav. Used to persist and apply the manager-chosen display order.
export type PageKey =
  | 'menu'
  | 'contact'
  | 'about'
  | 'press'
  | 'events'
  | 'catering'
  | 'careers'
  | 'locations'
  | 'gift-cards';

export interface PageDefinitionInterface {
  id: number;
  // Stable cross-system key used to persist display order: a PageKey, or a custom page's lowercased
  // title (see customPageKey).
  key: string;
  label: string;
  title: string;
  // Route to the page's editor. Only About and Press are editable — every other row omits this and
  // therefore shows no Edit action.
  route?: string;
  // Like `route`, but for an editor that lives under the brand (`/${brandId}/${brandRoute}`), so the
  // full path is only known at runtime. PagesPage resolves it into `route`. Not part of the Pages
  // section (see PAGE_SECTION_ROUTES), since these editors have their own sidebar item.
  brandRoute?: string;
  // Only shown when the current location's brand has more than one location.
  multiLocationOnly?: boolean;
  requiresGiftCardsLink?: boolean;
  // If set, Hide/Show toggles real backend visibility and reflects the live state.
  visibility?: PageVisibilityKey;
  customPageID?: number;
  // UI-only Hide/Show placeholder: toggles the row visually (session-only, not persisted) for pages
  // whose backend visibility endpoint isn't ready yet. Replace with `visibility` once it lands.
  localHide?: boolean;
  // Always-visible nav items (Menu, Contact). They are reorderable but can never be hidden, so the
  // row shows no Hide/Show action at all.
  alwaysVisible?: boolean;
}

/**
 * Single source of truth for the rows shown on the "Pages" screen — the navigation items of the
 * TapTab white-labeled website (plus the TapTab profile pages). The array order is the default
 * display order; drag-to-reorder persists a per-restaurant order via the /pageOrder API (keyed by
 * each row's `key`), which the website then applies to its nav.
 *
 * Per product: users can (1) Hide/Show About, Press, Events, Catering, Careers, Locations and Gift
 * Cards — Menu and Contact always show; (2) Edit About, Press, Locations and Gift Cards; (3) reorder
 * any row. Locations only appears when the brand has more than one location, and Gift Cards stays
 * greyed out until the brand has a valid gift cards link.
 *
 * NOTE: every `route` (and `brandRoute`) below must have a matching <Route> registered in
 * MainContainer.tsx — the Edit action navigates there, so an entry with no Route would hit NotFoundPage.
 */
export const PAGES: (PageDefinitionInterface & { key: PageKey })[] = [
  // Menu and Contact are always part of the website nav — reorderable but never hideable.
  { id: 1, key: 'menu', label: 'Menu', title: 'Your menu', alwaysVisible: true },
  // Multi-location brands only; defaults to second, per product. Edit opens the brand's Locations
  // screen. Its visibility backend isn't ready yet, so Hide/Show is UI-only.
  {
    id: 8,
    key: 'locations',
    label: 'Locations',
    title: 'Your locations',
    brandRoute: PAGE_LOCATIONS,
    multiLocationOnly: true,
    localHide: true
  },
  { id: 2, key: 'contact', label: 'Contact', title: 'Contact page', alwaysVisible: true },
  { id: 3, key: 'about', label: 'About', title: 'About your restaurant', route: PAGE_ABOUT, localHide: true },
  { id: 4, key: 'press', label: 'Press', title: 'Press & media', route: PAGE_PRESS, localHide: true },
  { id: 5, key: 'events', label: 'Events', title: 'Your events page', visibility: 'events' },
  // Catering has no manager editor on this frontend — Hide/Show only, wired to the backend.
  { id: 6, key: 'catering', label: 'Catering', title: 'Catering inquiry form', visibility: 'catering' },
  { id: 7, key: 'careers', label: 'Careers', title: 'Careers page', visibility: 'careers' },
  {
    id: 9,
    key: 'gift-cards',
    label: 'Gift Cards',
    title: 'Gift cards',
    brandRoute: PAGE_BRAND_GIFT_CARDS,
    requiresGiftCardsLink: true,
    localHide: true
  }
];

/**
 * Reorder rows to match the manager's saved page-key order. Keys in the saved order lead, in that
 * order. A page the saved order doesn't mention (one added since the restaurant last saved, e.g.
 * Locations) goes to its default position in PAGES rather than the end, so new pages land where
 * product placed them; a custom page goes to the end. A no-op for an empty saved order (restaurant never reordered).
 */
export const applySavedPageOrder = <T extends { key: string }>(rows: T[], order: string[]): T[] => {
  if (!order.length) return rows;
  const rank = new Map(order.map((key, index) => [key, index]));
  const defaultIndex = (row: T) => {
    const index = PAGES.findIndex((page) => page.key === row.key);
    return index === -1 ? Number.MAX_SAFE_INTEGER : index;
  };
  const saved = rows.filter((row) => rank.has(row.key)).sort((a, b) => rank.get(a.key)! - rank.get(b.key)!);
  // Inserted in default order, so each one's default index is already correct relative to the
  // pages inserted before it.
  return rows
    .filter((row) => !rank.has(row.key))
    .sort((a, b) => defaultIndex(a) - defaultIndex(b))
    .reduce((result, row) => insertAtIndex(result, Math.min(defaultIndex(row), result.length), row), saved);
};

/**
 * Editor routes that live "under" the Pages section. Used to keep the PAGES sidebar item
 * highlighted while editing any individual page. (Pages without a route are excluded.)
 */
export const PAGE_SECTION_ROUTES: string[] = PAGES.map((page) => page.route).filter((route): route is string =>
  Boolean(route)
);

/**
 * Whether a pathname belongs to the Pages section — the hub route itself or any individual page's
 * editor route. Keeps the PAGES sidebar item highlighted (shared by desktop + mobile sidebars).
 */
export const isPagesRoute = (pathname: string): boolean =>
  pathname === `/${PAGE_PAGES}` ||
  pathname.startsWith(`/${PAGE_PAGES}/`) ||
  pathname === `/${PAGE_EVENTS}` ||
  pathname.startsWith(`/${PAGE_EVENTS}/`) ||
  PAGE_SECTION_ROUTES.some((route) => pathname === `/${route}` || pathname.startsWith(`/${route}/`));
